import React, { useState, useEffect, useContext } from 'react';
import UserContext from '../../components/User_Context.js';
import * as menu from "../../assets/menus/menu.js"
import * as breadcrumb from "../../components/ui/breadcrumb.js";
import * as api from "../../api/apiConnector.js";
import waiting from "../../assets/images/waiting.svg";
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import * as utils from "../../components/utils.js";
import { useTranslation } from 'react-i18next';
import { SystemLabels } from '../_label/system/system_labels.js';


const Rights_Tree_Screen = (params) => {
    const { currentUser } = useContext(UserContext);
    const [data, setData] = useState(null);
    const [showChart, setShowChart] = useState(true);
    const [expandedNodes, setExpandedNodes] = useState({});
    const [editingNode, setEditingNode] = useState(null);
    const { t } = useTranslation();

    // Initialize i18n labels from centralized file
    const i18n = menu.getI18n();
    i18n.addResourceBundle('it', 'labels', SystemLabels.it, true, true);
    i18n.addResourceBundle('en', 'labels', SystemLabels.en, true, true);

    const setBreadCrumb = (params) => {
        var path = [{
            link: "/",
            name: t('home', { ns: 'labels' })
        },
        {
            link: "/admin/rights_tree",
            name: t('admin', { ns: 'labels' })
        },
        {
            link: "/admin/rights_tree",
            name: t('rightsTree', { ns: 'labels' })
        }
        ];
        breadcrumb.set(path);
    }

    const retrieveData = async () => {
        try {
            const rightsTreeResult = await api.sendMessage("security/rightsTree/getTree", {
                user: currentUser(),
                body: {}
            });

            if (rightsTreeResult?.success && rightsTreeResult?.rightsTree) {
                setData(rightsTreeResult.rightsTree);
                // Expand all nodes by default
                const expanded = {};
                const expandAllNodes = (nodes) => {
                    nodes?.forEach(node => {
                        expanded[node.Id] = true;
                        if (node.Children) {
                            expandAllNodes(node.Children);
                        }
                    });
                };
                expandAllNodes(rightsTreeResult.rightsTree);
                setExpandedNodes(expanded);
            }
        } catch (error) {
            console.error("Error loading rights tree:", error);
        }
    }

    useEffect(() => {
        (async () => {
            setBreadCrumb();
            menu.write({ user: currentUser() });
            await retrieveData();
        })();
    }, []);

    const _toSave = () => {
        const flattenNodes = (nodes, parentId = null, parentLevel = 0) => {
            let result = [];
            nodes?.forEach(node => {
                // Skip new nodes that are deleted - they don't need to be sent to backend
                if (node.Deleted && node.__isNew) {
                    return;
                }
                
                // Calculate level: use existing level for existing nodes, or determine level for new nodes
                let currentLevel;
                if (node.__isNew) {
                    // For new nodes: if parentId is null, it's a root node (level 0)
                    // Otherwise, it's parentLevel + 1
                    currentLevel = parentId === null ? 0 : parentLevel + 1;
                } else {
                    // For existing nodes, use their current level
                    currentLevel = node.Level;
                }
                
                let nodeData;
                
                if (node.Deleted) {
                    // Deleted nodes: only send Id and Deleted flag
                    nodeData = {
                        Id: node.Id,
                        Deleted: true
                    };
                } else if (node.__isNew) {
                    // New nodes: no ID, backend will generate it
                    nodeData = {
                        Name: node.Name,
                        Description: node.Description || '',
                        Level: currentLevel,
                        ParentId: (parentId && parentId.startsWith('temp_')) ? null : parentId,
                        // Add temporary parent reference for backend to reconstruct hierarchy
                        TempParentId: (parentId && parentId.startsWith('temp_')) ? parentId : null,
                        // Add original temp ID for mapping
                        OriginalTempId: node.Id
                    };
                } else {
                    // Modified existing nodes: include ID
                    nodeData = {
                        Id: node.Id,
                        Name: node.Name,
                        Description: node.Description || '',
                        Level: currentLevel,
                        ParentId: parentId
                    };
                }
                
                // Add original node data with metadata for filtering
                const nodeWithMeta = {
                    originalNode: node,
                    nodeData: nodeData
                };
                
                result.push(nodeWithMeta);
                
                // Process children, but skip children of deleted new nodes
                if (node.Children && !(node.Deleted && node.__isNew)) {
                    result = result.concat(flattenNodes(node.Children, node.Id, currentLevel));
                }
            });
            return result;
        };
        
        // First flatten all nodes, then filter only those that need to be updated
        const allNodes = flattenNodes(data);
        const nodesToUpdate = allNodes.filter(x => x.originalNode.__toUpdate);
        
        // Sort nodes by level to ensure parents are processed before children
        const sortedNodes = nodesToUpdate.sort((a, b) => {
            // First sort by level
            if (a.nodeData.Level !== b.nodeData.Level) {
                return a.nodeData.Level - b.nodeData.Level;
            }
            // Then prioritize nodes without TempParentId (existing parents)
            if (a.nodeData.TempParentId && !b.nodeData.TempParentId) {
                return 1;
            }
            if (!a.nodeData.TempParentId && b.nodeData.TempParentId) {
                return -1;
            }
            return 0;
        });
        
        // Return only the node data for backend
        return sortedNodes.map(x => x.nodeData);
    }

    const _validate = () => {
        const errors = [];
        const flattenNodes = (nodes) => {
            let result = [];
            nodes?.forEach(node => {
                // Skip deleted nodes in validation
                if (!node.Deleted) {
                    result.push(node);
                    if (node.Children) {
                        result = result.concat(flattenNodes(node.Children));
                    }
                }
            });
            return result;
        };
        
        const allNodes = flattenNodes(data) || [];
        
        for (const d of allNodes) {
            if (!d.Name || d.Name.trim() === '') {
                var error = t('missingNameForRight', { ns: 'labels', index: allNodes.findIndex(x => x === d) + 1 });
                errors.push(error);
            }
        }

        return errors;
    }

    const _save = async () => {
        // Validate first, before showing any saving popup
        const errors = _validate();

        if (errors.length > 0) {
            withReactContent(Swal).fire({
                title: t('error', { ns: 'labels' }),
                icon: "error",
                text: errors.join(', '),
                showConfirmButton: true
            });
            return { success: false };
        }

        const dataToUpdate = _toSave();

        if (dataToUpdate.length == 0) {
            withReactContent(Swal).fire({
                title: t('nothingToSave', { ns: 'labels' }),
                icon: "info",
                text: t('nothingToSaveMessage', { ns: 'labels' }),
                showConfirmButton: true
            });
            return { success: false };
        }

        // Show saving popup only if validation passes and there's data to save
        withReactContent(Swal).fire({
            title: t('saving', { ns: 'labels' }),
            icon: "info",
            text: t('savingNewData', { ns: 'labels' }),
            allowOutsideClick: false,
            showConfirmButton: false
        });

        try {
            const result = await api.sendMessage("admin/security/rights_tree/save_rights_tree", {
                user: currentUser(),
                body: {
                    rights: dataToUpdate
                }
            });

            if (result.success) {
                withReactContent(Swal).fire({
                    title: t('success', { ns: 'labels' }),
                    icon: "success",
                    text: t('savedSussessfullyWithNumberMessage', { ns: 'labels', number: dataToUpdate.length }),
                    showConfirmButton: true
                });

                const markAsNotUpdated = (nodes) => {
                    nodes?.forEach(node => {
                        node.__toUpdate = false;
                        if (node.Children) {
                            markAsNotUpdated(node.Children);
                        }
                    });
                };
                markAsNotUpdated(data);
                setData(utils.clone(data));

                return result;
            }
            else {
                if (result.messages?.length > 0) {
                    withReactContent(Swal).fire({
                        title: t('error', { ns: 'labels' }),
                        icon: "error",
                        text: result.messages.join(', '),
                        showConfirmButton: true
                    });
                    return result;
                } else {
                    withReactContent(Swal).fire({
                        title: t('error', { ns: 'labels' }),
                        icon: "error",
                        text: t(result.message.toLowerCase(), { ns: 'labels' }),
                        showConfirmButton: true
                    }).then(async (result) => {
                        if (result.isConfirmed) {
                            window.location.reload();
                        }
                    });
                }
            }
        } catch (error) {
            console.error("Error saving rights tree:", error);
            withReactContent(Swal).fire({
                title: t('error', { ns: 'labels' }),
                icon: "error",
                text: error.message || 'An unexpected error occurred',
                showConfirmButton: true
            });
            return { success: false };
        }
    }

    const updateData = (newNodes) => {
        setData(utils.clone(newNodes));
    }

    const toggleNode = (nodeId) => {
        setExpandedNodes(prev => ({
            ...prev,
            [nodeId]: !prev[nodeId]
        }));
    }

    const startEdit = (node) => {
        setEditingNode(node.Id);
    }

    const saveEdit = (node, newName, newDescription) => {
        const updateNode = (nodes) => {
            return nodes?.map(n => {
                if (n.Id === node.Id) {
                    return {
                        ...n,
                        Name: newName,
                        Description: newDescription,
                        __toUpdate: true
                    };
                }
                if (n.Children) {
                    return {
                        ...n,
                        Children: updateNode(n.Children)
                    };
                }
                return n;
            });
        };
        
        setData(updateNode(data));
        setEditingNode(null);
    }

    const cancelEdit = () => {
        setEditingNode(null);
    }

    const deleteNode = (nodeToDelete) => {
        withReactContent(Swal).fire({
            title: t('confirm', { ns: 'labels' }),
            text: t('confirmDeleteRight', { ns: 'labels', name: nodeToDelete.Name }),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('delete', { ns: 'labels' }),
            cancelButtonText: t('cancel', { ns: 'labels' })
        }).then((result) => {
            if (result.isConfirmed) {
                const markAsDeleted = (nodes) => {
                    return nodes?.map(n => {
                        if (n.Id === nodeToDelete.Id) {
                            // Mark this node and all its children as deleted
                            const markNodeAndChildrenDeleted = (node) => {
                                const deletedNode = {
                                    ...node,
                                    Deleted: true,
                                    __toUpdate: true
                                };
                                
                                if (node.Children) {
                                    deletedNode.Children = node.Children.map(child => markNodeAndChildrenDeleted(child));
                                }
                                
                                return deletedNode;
                            };
                            
                            return markNodeAndChildrenDeleted(n);
                        }
                        if (n.Children) {
                            return {
                                ...n,
                                Children: markAsDeleted(n.Children)
                            };
                        }
                        return n;
                    });
                };
                setData(markAsDeleted(data));
            }
        });
    }

    const addChildNode = (parentNode) => {
        const newNode = {
            Id: `temp_${Date.now()}`, // Temporary ID for frontend - will be removed before sending to backend
            Name: t('newRight', { ns: 'labels' }),
            Description: '',
            Children: [],
            __toUpdate: true,
            __isNew: true
        };

        if (parentNode.Id === null) {
            // Adding root node
            setData([...data, newNode]);
        } else {
            // Adding child node
            const addToParent = (nodes) => {
                return nodes?.map(n => {
                    if (n.Id === parentNode.Id) {
                        return {
                            ...n,
                            Children: [...(n.Children || []), newNode]
                        };
                    }
                    if (n.Children) {
                        return {
                            ...n,
                            Children: addToParent(n.Children)
                        };
                    }
                    return n;
                });
            };

            setData(addToParent(data));
            setExpandedNodes(prev => ({
                ...prev,
                [parentNode.Id]: true
            }));
        }
    }

    // Function to recursively render rights nodes with interactive controls
    const renderRightsNodes = (nodes, level = 0) => {
        if (!nodes || !Array.isArray(nodes)) {
            return null;
        }

        let result = [];

        nodes.forEach((node, index) => {
            // Skip deleted nodes
            if (node.Deleted) {
                return;
            }
            
            const hasChildren = node.Children && node.Children.filter(child => !child.Deleted).length > 0;
            const isExpanded = expandedNodes[node.Id];
            const isEditing = editingNode === node.Id;
            
            result.push(
                <tr key={node.Id || `node-${level}-${index}`} className={node.__toUpdate ? "table-warning" : ""} data-id={node.Id}>
                    <th scope="row">
                        {hasChildren && (
                            <i
                                className={`fa ${isExpanded ? 'fa-caret-down' : 'fa-caret-right'}`}
                                style={{ cursor: 'pointer', marginRight: '0.5em' }}
                                title={isExpanded ? t('Chiudi figli') : t('Apri figli')}
                                onClick={() => toggleNode(node.Id)}
                            />
                        )}
                    </th>
                    <td style={{ whiteSpace: "wrap" }}>
                        {isEditing ? (
                            <EditNodeForm
                                node={node}
                                onSave={saveEdit}
                                onCancel={cancelEdit}
                            />
                        ) : (
                            <div style={{ marginLeft: `${level * 1.5}em` }}>
                                {level < 3 ? (
                                    <h5>{node.Name || t('unnamed', { ns: 'labels' })}</h5>
                                ) : (
                                    <p>{node.Name || t('unnamed', { ns: 'labels' })}</p>
                                )}
                                {node.Description && (
                                    <small className="text-muted">{node.Description}</small>
                                )}
                            </div>
                        )}
                    </td>
                    <td>
                        {!isEditing && (
                            <div>
                                <i 
                                    style={{ cursor: 'pointer', color: '#17a2b8' }} 
                                    data-bs-tooltip="tooltip" 
                                    data-bs-placement='top' 
                                    title={t('edit', { ns: 'labels' })} 
                                    onClick={() => startEdit(node)}
                                    className="fa-regular fa-pen-to-square me-4 fa-2xl hover-icon-edit" 
                                />
                                <i 
                                    style={{ cursor: 'pointer' }} 
                                    data-bs-tooltip="tooltip" 
                                    data-bs-placement='top' 
                                    title={t('add', { ns: 'labels' })} 
                                    onClick={() => addChildNode(node)}
                                    className="fa-regular fa-square-plus me-4 fa-2xl hover-icon-add" 
                                />
                                <i 
                                    style={{ cursor: 'pointer', color: '#dc3545' }} 
                                    data-bs-tooltip="tooltip" 
                                    data-bs-placement='top' 
                                    title={t('delete', { ns: 'labels' })} 
                                    onClick={() => deleteNode(node)}
                                    className="fa fa-trash fa-2xl hover-icon-trash" 
                                />
                            </div>
                        )}
                    </td>
                </tr>
            );

            // Recursively render children if they exist and node is expanded
            if (hasChildren && isExpanded) {
                const childrenRows = renderRightsNodes(node.Children, level + 1);
                if (childrenRows) {
                    result = result.concat(childrenRows);
                }
            }
        });

        return result;
    }

    return (
        <div className="d-flex flex-column" style={{ height: '100vh' }}>
            <header className="bg-white shadow-sm">
                <div className="container-fluid d-flex justify-content-between align-items-center py-2">
                    <ol className="breadcrumb mb-0"></ol>
                    <div className="d-flex gap-3">
                        <a
                            className="text-primary fs-13"
                            onClick={(e) => {
                                _save();
                            }}
                            role="button"
                            title={t('saveThisData', { ns: 'labels' })}
                        >
                            <i className="fa-solid fa-floppy-disk fa-2x"></i>
                        </a>
                        <a
                            className="text-primary fs-13"
                            onClick={(e) => {
                                setShowChart(!showChart);
                            }}
                            role="button"
                            title={showChart ? t('switchToTreeView', { ns: 'labels' }) : t('switchToListView', { ns: 'labels' })}
                        >
                            <i className={`fa-solid fa-${showChart ? 'diagram-project' : 'list'} fa-2x`}></i>
                        </a>
                    </div>
                </div>
            </header>

            <main className="flex-grow-1 overflow-auto">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-xl-12">
                            <div className="card">
                                <div className="card-body p-0">
                                    <div className="tab-content" id="tabs-content" style={{ padding: 15 }}>
                                        {data ? (
                                            showChart ? (
                                                <div id="rightsTreeContainer">
                                                    <table className="table">
                                                        <thead>
                                                            <tr>
                                                                <th className="col-md-1" scope="col">#</th>
                                                                <th className="col-md-11">{t('name', { ns: 'labels' })}</th>
                                                                <th className="col-md-1">{t('actions', { ns: 'labels' })}</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody id="tBodyTable">
                                                            <tr data-root-node="true">
                                                                <td></td>
                                                                <td></td>
                                                                <td>
                                                                    <div>
                                                                        <i 
                                                                            style={{ cursor: 'pointer' }} 
                                                                            data-bs-tooltip="tooltip" 
                                                                            data-bs-placement='top' 
                                                                            title={t('add', { ns: 'labels' })} 
                                                                            onClick={() => addChildNode({ Id: null })}
                                                                            className="fa-regular fa-square-plus me-4 fa-2xl hover-icon-add" 
                                                                        />
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                            {renderRightsNodes(data)}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            ) : (
                                                <div>
                                                    <table className="table">
                                                        <thead>
                                                            <tr>
                                                                <th className="col-md-1" scope="col">#</th>
                                                                <th className="col-md-11">{t('name', { ns: 'labels' })}</th>
                                                                <th className="col-md-1">{t('actions', { ns: 'labels' })}</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody id="tBodyTable">
                                                            <tr data-root-node="true">
                                                                <td></td>
                                                                <td></td>
                                                                <td>
                                                                    <div>
                                                                        <i 
                                                                            style={{ cursor: 'pointer' }} 
                                                                            data-bs-tooltip="tooltip" 
                                                                            data-bs-placement='top' 
                                                                            title={t('add', { ns: 'labels' })} 
                                                                            onClick={() => addChildNode({ Id: null })}
                                                                            className="fa-regular fa-square-plus me-4 fa-2xl hover-icon-add" 
                                                                        />
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                            {renderRightsNodes(data)}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )
                                        ) : <div className="text-center"><img src={waiting} /></div>}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    )
}

// Component for editing node inline
const EditNodeForm = ({ node, onSave, onCancel }) => {
    const [name, setName] = useState(node.Name || '');
    const [description, setDescription] = useState(node.Description || '');
    const { t } = useTranslation();

    const handleSave = () => {
        onSave(node, name, description);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSave();
        } else if (e.key === 'Escape') {
            onCancel();
        }
    };

    return (
        <div className="d-flex gap-2 flex-grow-1">
            <input
                type="text"
                className="form-control form-control-sm"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={handleKeyPress}
                placeholder={t('name', { ns: 'labels' })}
                autoFocus
            />
            <input
                type="text"
                className="form-control form-control-sm"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                onKeyDown={handleKeyPress}
                placeholder={t('description', { ns: 'labels' })}
            />
            <button className="btn btn-sm btn-success" onClick={handleSave}>
                <i className="fas fa-check"></i>
            </button>
            <button className="btn btn-sm btn-secondary" onClick={onCancel}>
                <i className="fas fa-times"></i>
            </button>
        </div>
    );
};

export { Rights_Tree_Screen } 